import React, {FC, ReactElement, useState} from "react";
import {
    IonButton,
    IonCheckbox,
    IonIcon,
    IonInput,
    IonItem,
    IonPopover,
    IonSelect,
    IonSelectOption,
    IonText,
    IonTextarea,
} from "@ionic/react";
import {ErrorMessage} from "@hookform/error-message";
import {TextFieldTypes} from "@ionic/core";
import "./Input.css";
import {Control, Controller} from "react-hook-form";
import {eye, eyeOff, helpCircleOutline} from "ionicons/icons";

export interface InputProps {
    isVisible?: boolean;
    isDisabled?: boolean;
    name: any;
    helpText?: any;
    showLabel?: boolean;
    control?: Control;
    register?: any;
    hasControl?: boolean;
    label?: string;
    render?: any;
    minimum?: number;
    maximum?: number;
    type?:
        | TextFieldTypes
        | "select"
        | "checkbox"
        | "textarea"
        | "one-time-code";
    autofocus?: boolean;
    rules?: any;
    checkboxSetValue?: any;
    options?: { key: string | number; text: string }[];
    errors?: any;
}

export interface FormFieldInputs {
    [field: string]: InputProps
}

const Input: FC<InputProps> = ({
                                   isVisible = true,
                                   isDisabled = false,
                                   name,
                                   helpText,
                                   control,
                                   render,
                                   type,
                                   minimum,
                                   maximum,
                                   autofocus,
                                   hasControl = true,
                                   label,
                                   options,
                                   errors,
                                   rules,
                               }) => {
    const [showPassword, setShowPassword] = useState<boolean>(false);
    const [showHelpPopover, setShowHelpPopover] = useState<boolean>(false);

    const renderInput = (e: { field: any, fieldState: any, formState: any }) => {
        const {field} = e;
        const {onChange: controllerOnChange, value, ref} = field;

        const handleChange = (event: any) => {
            if (type === 'checkbox') {
                controllerOnChange(event.detail.checked);
            } else {
                const val = event.detail.value !== undefined ? event.detail.value : event.detail.checked;
                controllerOnChange(val !== undefined ? val : event);
            }
        };

        const toggleShowPassword = (event: any) => {
            event.preventDefault();
            setShowPassword(!showPassword);
        };

        // Determine min/max props for IonInput (only relevant for 'number' or 'date'):
        const applyMin = type === "number" || type === "date" ? minimum : undefined;
        const applyMax = type === "number" || type === "date" ? maximum : undefined;

        // If it's text, URL or textarea, we can pass `max` as `maxLength`
        const applyMaxLength = (type === "text" || type === "url" || type === "textarea") && maximum != null ? maximum : undefined;

        // "one-time-code" is not a valid IonInput `type` (it silently falls back
        // to text). It's an autocomplete hint — render a text input carrying
        // autocomplete="one-time-code" so SMS OTP autofill works (F56).
        const isOtp = type === "one-time-code";


        switch (type) {
            case "checkbox":
                return (
                    <IonCheckbox
                        disabled={isDisabled}
                        onIonChange={handleChange}
                        checked={value}
                        ref={ref}
                        labelPlacement="end"
                        justify="start"
                        aria-invalid={errors && errors[name] ? "true" : "false"}
                        aria-describedby={`${name}Error`}
                    >
                        {label}
                    </IonCheckbox>
                );

            case "select":
                return (
                    <IonSelect
                        key={value}
                        onIonChange={handleChange}
                        value={value}
                        ref={ref}
                        label={label}
                        labelPlacement={"stacked"}
                        placeholder={label}
                        disabled={isDisabled}
                        aria-invalid={errors && errors[name] ? "true" : "false"}
                        aria-describedby={`${name}Error`}
                    >
                        {options &&
                            options.map((optionItem, optionKey) => (
                                <IonSelectOption
                                    key={optionItem.key}
                                    value={optionItem.key}
                                >
                                    {optionItem.text}
                                </IonSelectOption>
                            ))}
                    </IonSelect>
                );

            case "textarea":
                return (
                    <IonTextarea
                        disabled={isDisabled}
                        name={name}
                        onIonInput={handleChange}
                        label={label}
                        labelPlacement={"stacked"}
                        placeholder={label}
                        value={value ?? ''}
                        maxlength={applyMaxLength}
                        aria-invalid={errors && errors[name] ? "true" : "false"}
                        aria-describedby={`${name}Error`}
                    />
                );

            default:
                return (
                    <>
                        <IonInput
                            disabled={isDisabled}
                            autofocus={autofocus}
                            name={name}
                            clearOnEdit={false}
                            onIonInput={handleChange}
                            label={label}
                            labelPlacement={"stacked"}
                            placeholder={label}
                            type={((type === "password" && showPassword) || isOtp ? "text" : type) as TextFieldTypes}
                            autocomplete={isOtp ? "one-time-code" : undefined}
                            value={value ?? ''}
                            min={applyMin}
                            max={applyMax}
                            maxlength={applyMaxLength}
                            aria-invalid={errors && errors[name] ? "true" : "false"}
                            aria-describedby={`${name}Error`}
                        />
                        {type === "password" && (
                            <IonButton fill="clear" onClick={toggleShowPassword}>
                                <IonIcon slot="icon-only" size={"large"} icon={showPassword ? eye : eyeOff}/>
                            </IonButton>
                        )}
                    </>
                );
        }
    };

    const renderNoControl = (render: () => ReactElement) => {
        return render();
    };

    return (
        isVisible ? (
            <>
                <IonItem>
                    {hasControl === false && renderNoControl(render)}
                    {hasControl && (
                        <Controller
                            render={render ?? renderInput}
                            control={control}
                            name={name}
                            rules={rules}
                        />
                    )}
                    {helpText && (
                        <IonButton fill="clear" size={"large"} onClick={() => setShowHelpPopover(true)}>
                            <IonIcon slot="icon-only" icon={helpCircleOutline} />
                        </IonButton>
                    )}
                    <IonPopover
                        className="input-popover"
                        isOpen={showHelpPopover}
                        onDidDismiss={() => setShowHelpPopover(false)}
                    >
                        <div className="input-popover-content">
                            <div className="input-popover-header">
                                <IonIcon className="help-icon" icon={helpCircleOutline}/>
                                <span className="input-popover-label">{label}</span>
                            </div>
                            <div className="input-popover-body">
                                {helpText}
                            </div>
                        </div>
                    </IonPopover>
                </IonItem>
                <IonText color="danger">
                    <ErrorMessage errors={errors} name={name}/>
                </IonText>
            </>
        ) : null
    );
};

export default Input;
